import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Shield,
  Users,
  Plus,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  MoreVertical
} from 'lucide-react';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  users: number;
}

interface Permission {
  id: string;
  name: string;
  description: string;
  module: string;
}

const mockRoles: Role[] = [
  {
    id: '1',
    name: 'Administrador',
    description: 'Acesso total ao sistema',
    permissions: ['all'],
    users: 3
  },
  {
    id: '2',
    name: 'Editor',
    description: 'Gerenciamento de conteúdo',
    permissions: ['content.read', 'content.write', 'content.publish'],
    users: 8
  },
  {
    id: '3',
    name: 'Autor',
    description: 'Criação de conteúdo',
    permissions: ['content.read', 'content.write'],
    users: 15
  },
  {
    id: '4',
    name: 'Visualizador',
    description: 'Apenas visualização',
    permissions: ['content.read'],
    users: 42
  }
];

const mockPermissions: Permission[] = [
  {
    id: 'content.read',
    name: 'Visualizar Conteúdo',
    description: 'Permite visualizar todo o conteúdo',
    module: 'Conteúdo'
  },
  {
    id: 'content.write',
    name: 'Editar Conteúdo',
    description: 'Permite criar e editar conteúdo',
    module: 'Conteúdo'
  },
  {
    id: 'content.publish',
    name: 'Publicar Conteúdo',
    description: 'Permite publicar e despublicar conteúdo',
    module: 'Conteúdo'
  },
  {
    id: 'users.manage',
    name: 'Gerenciar Usuários',
    description: 'Permite gerenciar usuários e permissões',
    module: 'Usuários'
  },
  {
    id: 'settings.manage',
    name: 'Gerenciar Configurações',
    description: 'Permite alterar configurações do sistema',
    module: 'Sistema'
  }
];

const Permissions = () => {
  const [roles, setRoles] = useState(mockRoles);
  const [permissions] = useState(mockPermissions);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  const handleDeleteRole = (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta função?')) {
      setRoles(prev => prev.filter(role => role.id !== id));
    }
  };

  const handleSaveRole = (role: Role) => {
    if (role.id) {
      setRoles(prev =>
        prev.map(r => (r.id === role.id ? role : r))
      );
    } else {
      setRoles(prev => [
        ...prev,
        { ...role, id: String(Date.now()) }
      ]);
    }
    setEditingRole(null);
  };

  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    role.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-28 pb-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-bold">Permissões</h1>
          </div>
          <button
            onClick={() => setEditingRole({
              id: '',
              name: '',
              description: '',
              permissions: [],
              users: 0
            })}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nova Função
          </button>
        </div>

        <div className="grid lg:grid-cols-[1fr,300px] gap-8">
          {/* Roles List */}
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Pesquisar funções..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-secondary rounded-lg focus:ring-2 focus:ring-primary focus:outline-none"
                />
              </div>
              <button className="p-2 hover:bg-secondary rounded-lg transition-colors">
                <Filter className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {filteredRoles.map((role) => (
                <motion.div
                  key={role.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-card p-4 rounded-lg hover:ring-1 hover:ring-primary/50 transition-all"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium">{role.name}</h3>
                      <p className="text-sm text-gray-400 mt-1">{role.description}</p>
                      <div className="flex items-center gap-4 mt-4">
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Users className="w-4 h-4" />
                          {role.users} usuários
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-400">
                          <Shield className="w-4 h-4" />
                          {role.permissions.length} permissões
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingRole(role)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedRole(role)}
                        className="p-2 hover:bg-secondary rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}

              {filteredRoles.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhuma função encontrada</p>
                </div>
              )}
            </div>
          </div>

          {/* Permissions List */}
          <div className="bg-card p-6 rounded-lg">
            <h2 className="text-lg font-semibold mb-4">Permissões Disponíveis</h2>
            <div className="space-y-4">
              {permissions.map((permission) => (
                <div
                  key={permission.id}
                  className="p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{permission.name}</h4>
                      <p className="text-sm text-gray-400 mt-1">{permission.description}</p>
                      <span className="inline-block mt-2 px-2 py-1 bg-primary/10 text-primary rounded text-xs">
                        {permission.module}
                      </span>
                    </div>
                    <div className="shrink-0">
                      {selectedRole?.permissions.includes(permission.id) ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Permissions;